import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react'
import {Box, Typography} from '@mui/material'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

/**
 * Canvas signature pad — port of SignatureModal from activities-page-web-component
 * (EditSignatureWrapper): smooth strokes via quadratic Bézier curves and a PNG
 * export trimmed to the drawn area.
 */

export interface SignaturePadHandle {
  clear: () => void
  /** PNG data URL trimmed to the signature bounds, or null when nothing was drawn. */
  getTrimmedDataUrl: () => string | null
}

interface SignaturePadNewProps {
  label?: string
  onChange?: (hasSigned: boolean) => void
  height?: number
  disabled?: boolean
}

const STROKE_COLOR = '#000000'
const TRIM_PADDING = 10

type Point = {x: number; y: number}

function getTrimmedDataUrl(canvas: HTMLCanvasElement): string | null {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const {width, height} = canvas
  const {data} = ctx.getImageData(0, 0, width, height)

  let minX = width
  let minY = height
  let maxX = 0
  let maxY = 0
  let foundPixel = false

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        minX = Math.min(minX, x)
        minY = Math.min(minY, y)
        maxX = Math.max(maxX, x)
        maxY = Math.max(maxY, y)
        foundPixel = true
      }
    }
  }

  if (!foundPixel) return null

  minX = Math.max(0, minX - TRIM_PADDING)
  minY = Math.max(0, minY - TRIM_PADDING)
  maxX = Math.min(width, maxX + TRIM_PADDING)
  maxY = Math.min(height, maxY + TRIM_PADDING)

  const trimmedWidth = maxX - minX
  const trimmedHeight = maxY - minY
  const tempCanvas = document.createElement('canvas')
  const tempCtx = tempCanvas.getContext('2d')
  if (!tempCtx) return canvas.toDataURL('image/png')

  tempCanvas.width = trimmedWidth
  tempCanvas.height = trimmedHeight
  tempCtx.drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight)

  return tempCanvas.toDataURL('image/png')
}

const SignaturePadNew = forwardRef<SignaturePadHandle, SignaturePadNewProps>(function SignaturePadNew(
  {label, onChange, height = 200, disabled = false},
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawing = useRef(false)
  const points = useRef<Point[]>([])
  const hasSigned = useRef(false)
  // Keeps the latest callbacks reachable from the native listeners registered once.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  const setHasSigned = (value: boolean) => {
    if (hasSigned.current === value) return
    hasSigned.current = value
    onChangeRef.current?.(value)
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) {
      // clearRect is affected by the dpr scale: with dpr < 1 (browser zoomed out) it would
      // leave the right and bottom edges dirty, so wipe the bitmap in device pixels.
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.restore()
      ctx.beginPath()
    }
    isDrawing.current = false
    points.current = []
    setHasSigned(false)
  }

  useImperativeHandle(ref, () => ({
    clear,
    getTrimmedDataUrl: () => {
      const canvas = canvasRef.current
      return canvas && hasSigned.current ? getTrimmedDataUrl(canvas) : null
    },
  }))

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const getCoords = (event: MouseEvent | TouchEvent): Point => {
      const rect = canvas.getBoundingClientRect()
      const source = 'touches' in event && event.touches.length > 0 ? event.touches[0] : event as MouseEvent
      return {x: source.clientX - rect.left, y: source.clientY - rect.top}
    }

    const setCanvasSize = () => {
      const {width, height: canvasHeight} = canvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      canvas.width = width * dpr
      canvas.height = canvasHeight * dpr
      ctx.scale(dpr, dpr)
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = STROKE_COLOR
      // Resizing wipes the bitmap: keep the "signed" flag in sync with what's visible.
      setHasSigned(false)
    }

    const startDrawing = (event: MouseEvent | TouchEvent) => {
      if (disabledRef.current) return
      event.preventDefault()
      const point = getCoords(event)
      points.current = [point]
      ctx.beginPath()
      ctx.moveTo(point.x, point.y)
      isDrawing.current = true
      setHasSigned(true)
    }

    const draw = (event: MouseEvent | TouchEvent) => {
      if (!isDrawing.current) return
      event.preventDefault()
      points.current.push(getCoords(event))
      const len = points.current.length
      if (len < 2) return
      const p1 = points.current[len - 2]
      const p2 = points.current[len - 1]
      ctx.quadraticCurveTo(p1.x, p1.y, (p1.x + p2.x) / 2, (p1.y + p2.y) / 2)
      ctx.stroke()
    }

    const stopDrawing = () => {
      isDrawing.current = false
      points.current = []
    }

    setCanvasSize()
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(setCanvasSize) : undefined
    resizeObserver?.observe(canvas)

    canvas.addEventListener('mousedown', startDrawing)
    canvas.addEventListener('mousemove', draw)
    canvas.addEventListener('mouseup', stopDrawing)
    canvas.addEventListener('mouseleave', stopDrawing)
    canvas.addEventListener('touchstart', startDrawing, {passive: false})
    canvas.addEventListener('touchmove', draw, {passive: false})
    canvas.addEventListener('touchend', stopDrawing)
    canvas.addEventListener('touchcancel', stopDrawing)

    return () => {
      resizeObserver?.disconnect()
      canvas.removeEventListener('mousedown', startDrawing)
      canvas.removeEventListener('mousemove', draw)
      canvas.removeEventListener('mouseup', stopDrawing)
      canvas.removeEventListener('mouseleave', stopDrawing)
      canvas.removeEventListener('touchstart', startDrawing)
      canvas.removeEventListener('touchmove', draw)
      canvas.removeEventListener('touchend', stopDrawing)
      canvas.removeEventListener('touchcancel', stopDrawing)
    }
  // Listeners are registered once per mount; callbacks are read through refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, width: '100%'}}>
      {label && (
        <Typography sx={{fontSize: 14, fontWeight: 600, color: NEUTRAL_SCALE[900]}}>{label}</Typography>
      )}
      <Box
        sx={{
          width: '100%',
          height,
          backgroundColor: NEUTRAL_SCALE[50],
          border: `2px dashed ${NEUTRAL_SCALE[300]}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label={label ?? 'Area firma'}
          data-testid="signature-pad-canvas"
          style={{width: '100%', height: '100%', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair', display: 'block'}}
        />
      </Box>
      <Typography sx={{fontSize: 12, color: NEUTRAL_SCALE[500]}}>Usa il mouse o il dito per firmare.</Typography>
    </Box>
  )
})

export default SignaturePadNew
